import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can, ORG_LIST_LIMIT, type AdminOrg } from '../lib/adminApi'
import { makeLabeler, makeRegionLabeler, useCommonCodes, useRegions } from '../lib/codes'

/**
 * 기업 관리 목록 — 비공개 기업을 포함한다.
 *
 * 검수 화면(`/admin`)과 나눠 둔 이유: 저기는 **이미 있는 카드를 공개할지 판단**하는 곳이고
 * 여기는 **카드를 만드는** 곳이다. 08-14에 도착할 기업 원천 데이터가 들어올 문이 여기다.
 */

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminOrgListPage() {
  const me = useAdminMe()
  const queryClient = useQueryClient()

  const mayWrite = can(me.data, 'org', 'write')
  const mayPublish = can(me.data, 'card', 'publish')

  const orgTypes = useCommonCodes('org_type')
  const regions = useRegions()
  const orgs = useQuery({ queryKey: ['admin-orgs'], queryFn: () => adminApi.orgs({ limit: ORG_LIST_LIMIT }) })

  const orgTypeLabel = makeLabeler(orgTypes.data)
  const regionLabel = makeRegionLabeler(regions.data)

  const publish = useMutation({
    mutationFn: (v: { id: string; isPublished: boolean }) =>
      adminApi.setOrgPublished(v.id, v.isPublished),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] }),
  })

  const actionError = errorMessage(publish.error)
  const items: AdminOrg[] = orgs.data?.items ?? []

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">기업 관리</h1>
          <p className="page-desc">
            기업을 등록하고 해결카드를 붙입니다. <b>1기업 : N해결카드</b>이고, 매칭 단위는
            기업이 아니라 개별 해결카드입니다.
          </p>
        </div>
        {mayWrite && (
          <Link className="btn" to="/admin/orgs/new">
            + 새 기업
          </Link>
        )}
      </div>

      {!mayWrite && (
        <div className="notice">
          읽기 권한만 있는 계정입니다. 등록·수정은 <b>org.write</b> 권한이 있어야 합니다.
        </div>
      )}

      {actionError !== null && (
        <div className="notice error" role="alert">
          {actionError}
        </div>
      )}

      <QueryState isPending={orgs.isPending} error={orgs.error} isEmpty={items.length === 0}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>기업명</th>
              <th>유형</th>
              <th>거점</th>
              <th>공개 카드</th>
              <th>공개</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id}>
                <td>
                  {mayWrite ? <Link to={`/admin/orgs/${o.id}`}>{o.name}</Link> : <b>{o.name}</b>}
                  {o.operationStatus !== 'active' && (
                    <div className="muted small">
                      {o.operationStatus === 'paused' ? '운영 중단' : '종료'}
                    </div>
                  )}
                </td>
                <td className="muted small">{orgTypeLabel(o.orgType)}</td>
                <td className="muted small">{regionLabel(o.baseRegionCode)}</td>
                <td className="muted small">{o.cardCount}</td>
                <td>{o.isPublished ? '공개' : '비공개'}</td>
                <td>
                  <div className="row-actions">
                    {mayWrite && (
                      <Link className="btn ghost small-btn" to={`/admin/cards/new?orgId=${o.id}`}>
                        카드 추가
                      </Link>
                    )}
                    {mayPublish ? (
                      <button
                        type="button"
                        className="btn ghost small-btn"
                        disabled={publish.isPending}
                        onClick={() => publish.mutate({ id: o.id, isPublished: !o.isPublished })}
                      >
                        {o.isPublished ? '내리기' : '공개'}
                      </button>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </div>
  )
}
